import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'
import { useToast } from '@/hooks/useToast'

interface BulkResult {
  created: number
  failed: number
  results: Array<{ shortUrl: string; shortcode: string; original_url: string }>
  errors: Array<{ index: number; url: string; error: string }>
}

interface Props {
  isOpen: boolean
  onClose: () => void
}

export function BulkCreateModal({ isOpen, onClose }: Props) {
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const [input, setInput] = useState('')
  const [result, setResult] = useState<BulkResult | null>(null)

  const bulkMutation = useMutation({
    mutationFn: (urls: string[]) =>
      apiRequest<BulkResult>('/api/shorten/bulk', 'POST', { urls }),
    onSuccess: (data) => {
      setResult(data)
      showToast(`${data.created} links created${data.failed ? `, ${data.failed} failed` : ''}`)
      queryClient.invalidateQueries({ queryKey: ['urls'] })
      queryClient.invalidateQueries({ queryKey: ['overview'] })
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Bulk create failed', 'error')
    },
  })

  const handleSubmit = () => {
    const urls = input
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)

    if (urls.length === 0) {
      showToast('Enter at least one URL', 'error')
      return
    }
    if (urls.length > 50) {
      showToast('Maximum 50 URLs per batch', 'error')
      return
    }

    bulkMutation.mutate(urls)
  }

  const handleClose = () => {
    setInput('')
    setResult(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white dark:bg-[#1c2230] rounded-2xl shadow-xl w-full max-w-lg p-6 animate-slideUp max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0d121b] dark:text-white">Bulk Create Links</h3>
          <button
            onClick={handleClose}
            className="p-1 text-[#4c669a] hover:text-[#0d121b] dark:hover:text-white rounded-lg"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {!result ? (
          <>
            <p className="text-sm text-[#4c669a] dark:text-gray-400 mb-3">
              Enter one URL per line (max 50).
            </p>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={`https://example.com\nhttps://another-site.com/page\nhttps://docs.example.com`}
              rows={8}
              className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-[#e7ebf3] dark:border-white/10 rounded-lg text-sm text-[#0d121b] dark:text-white placeholder:text-[#4c669a] focus:ring-2 focus:ring-[#1754cf] focus:border-transparent font-mono resize-none"
            />
            <p className="text-xs text-[#4c669a] mt-1 mb-4">
              {input.split('\n').filter(l => l.trim()).length} URL(s) entered
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2.5 text-sm font-medium text-[#4c669a] hover:text-[#0d121b] dark:hover:text-white border border-[#e7ebf3] dark:border-white/10 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={bulkMutation.isPending || !input.trim()}
                className="px-4 py-2.5 text-sm font-bold text-white bg-[#1754cf] rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {bulkMutation.isPending && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                Create {input.split('\n').filter(l => l.trim()).length} Links
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                {result.created} link(s) created{result.failed > 0 && `, ${result.failed} failed`}
              </p>
            </div>

            {result.results.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-[#4c669a] uppercase mb-2">Created</p>
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                  {result.results.map(r => (
                    <div key={r.shortcode} className="flex items-center justify-between text-sm bg-[#f6f6f8] dark:bg-white/5 px-3 py-2 rounded-lg">
                      <span className="font-medium text-[#1754cf] font-mono">{r.shortUrl}</span>
                      <button
                        onClick={async () => {
                          await navigator.clipboard.writeText(r.shortUrl)
                          showToast('Copied!')
                        }}
                        className="text-[#4c669a] hover:text-[#0d121b] dark:hover:text-white"
                      >
                        <span className="material-symbols-outlined text-[16px]">content_copy</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-red-500 uppercase mb-2">Failed</p>
                <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-sm bg-red-50 dark:bg-red-900/10 px-3 py-2 rounded-lg">
                      <span className="text-red-600 dark:text-red-400 font-mono text-xs break-all">{e.url}</span>
                      <span className="text-red-500 text-xs ml-2">— {e.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2.5 text-sm font-bold text-white bg-[#1754cf] rounded-lg hover:opacity-90"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
